import type {
  ComparisonBasis,
  IPeriodFilter,
  IResolvedPeriod,
  MonthKey,
  PeriodGrain,
  PeriodOverride,
} from '../types/period';
import { MonthKeyHelper } from './MonthKeyHelper';

export const PERIOD_GRAIN_LABELS: Record<PeriodGrain, string> = {
  month: 'Month',
  quarter: 'Quarter',
  ytd: 'Year to date',
  ttm: 'Trailing 12 months',
};

export const COMPARISON_BASIS_LABELS: Record<ComparisonBasis, string> = {
  prior_period: 'Prior period',
  prior_year: 'Prior year',
  trailing_3_avg: 'Trailing-3 average',
};

/** Resolves period filters into concrete month windows, capped at the books-closed month. */
export class PeriodHelper {
  /**
   * Every window is capped at `booksClosedThrough`; months past it are reported
   * in `openMonths` and never returned in `months`, so nothing downstream can aggregate them.
   */
  static resolve(
    filter: IPeriodFilter,
    booksClosedThrough: MonthKey,
    fiscalYearStartMonth = 1
  ): IResolvedPeriod {
    const requestedAnchor = filter.anchor;
    const clamped = requestedAnchor > booksClosedThrough;
    const anchor = clamped ? booksClosedThrough : requestedAnchor;
    const explicitRange = Boolean(filter.rangeStart);

    const months = filter.rangeStart
      ? MonthKeyHelper.range(
          filter.rangeStart > anchor ? anchor : filter.rangeStart,
          anchor
        )
      : PeriodHelper.windowFor(filter.grain, anchor, fiscalYearStartMonth);

    const openMonths = clamped
      ? MonthKeyHelper.range(
          MonthKeyHelper.add(booksClosedThrough, 1),
          requestedAnchor
        )
      : [];

    const comparisonWindows = PeriodHelper.comparisonWindows(
      months,
      filter.comparison
    );
    const labelFor = (window: MonthKey[]) =>
      PeriodHelper.windowLabel(
        window,
        filter.grain,
        explicitRange,
        fiscalYearStartMonth
      );

    return {
      months,
      comparisonWindows,
      comparison: filter.comparison,
      label: labelFor(months),
      comparisonLabel:
        filter.comparison === 'trailing_3_avg'
          ? `avg. of 3 prior ${months.length === 1 ? 'months' : 'periods'}`
          : `vs ${labelFor(comparisonWindows[0] ?? [])}`,
      clamped,
      requestedAnchor,
      openMonths,
    };
  }

  /** The primary window widened to at least `min` months ending at the anchor, for trend charts. */
  static trendMonths(resolved: IResolvedPeriod, min = 12): MonthKey[] {
    const last = resolved.months[resolved.months.length - 1];
    if (!last) return [];
    if (resolved.months.length >= min) return resolved.months;
    return MonthKeyHelper.range(MonthKeyHelper.add(last, -(min - 1)), last);
  }

  static mergeFilter(
    base: IPeriodFilter,
    override?: PeriodOverride | null
  ): IPeriodFilter {
    if (!override) return base;
    return {
      grain: override.grain ?? base.grain,
      anchor: override.anchor ?? base.anchor,
      rangeStart:
        override.rangeStart === undefined
          ? base.rangeStart
          : override.rangeStart,
      comparison: override.comparison ?? base.comparison,
      includeOpenMonths:
        override.includeOpenMonths ?? base.includeOpenMonths ?? false,
    };
  }

  private static windowFor(
    grain: PeriodGrain,
    anchor: MonthKey,
    fiscalYearStartMonth: number
  ): MonthKey[] {
    switch (grain) {
      case 'month':
        return [anchor];
      case 'quarter': {
        const { index, fiscalYear } = MonthKeyHelper.quarterOf(
          anchor,
          fiscalYearStartMonth
        );
        const start = MonthKeyHelper.add(
          MonthKeyHelper.from(fiscalYear, fiscalYearStartMonth),
          (index - 1) * 3
        );
        return MonthKeyHelper.range(start, anchor);
      }
      case 'ytd': {
        const { fiscalYear } = MonthKeyHelper.quarterOf(
          anchor,
          fiscalYearStartMonth
        );
        return MonthKeyHelper.range(
          MonthKeyHelper.from(fiscalYear, fiscalYearStartMonth),
          anchor
        );
      }
      case 'ttm':
        return MonthKeyHelper.range(MonthKeyHelper.add(anchor, -11), anchor);
      default:
        return [anchor];
    }
  }

  private static comparisonWindows(
    months: MonthKey[],
    comparison: ComparisonBasis
  ): MonthKey[][] {
    const len = months.length;
    const shift = (delta: number) =>
      months.map((m) => MonthKeyHelper.add(m, delta));
    switch (comparison) {
      case 'prior_period':
        return [shift(-len)];
      case 'prior_year':
        return [shift(-12)];
      case 'trailing_3_avg':
        return [shift(-len), shift(-2 * len), shift(-3 * len)];
      default:
        return [];
    }
  }

  private static windowLabel(
    months: MonthKey[],
    grain: PeriodGrain,
    explicitRange: boolean,
    fiscalYearStartMonth: number
  ): string {
    const first = months[0];
    const last = months[months.length - 1];
    if (!first || !last) return '—';
    if (months.length === 1) return MonthKeyHelper.label(first);
    if (!explicitRange && grain === 'quarter') {
      const q = MonthKeyHelper.quarterOf(last, fiscalYearStartMonth);
      const fySuffix =
        fiscalYearStartMonth !== 1
          ? `/${String(q.fiscalYear + 1).slice(2)}`
          : '';
      return `Q${q.index} FY${q.fiscalYear}${fySuffix}`;
    }
    if (!explicitRange && grain === 'ytd')
      return `YTD ${MonthKeyHelper.label(last)}`;
    if (!explicitRange && grain === 'ttm')
      return `TTM ${MonthKeyHelper.label(last)}`;
    return `${MonthKeyHelper.shortLabel(first)} – ${MonthKeyHelper.shortLabel(last)}`;
  }
}
